import Swiper from 'swiper';
import slugify from 'slugify';
import { Controller, EffectFade, Keyboard } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
import { calculateDistance, isMobileView, presetInputByUrlParams, renderStringAsHtml, setFilterInUrlParam, setHash, stringToLocationList } from '../helper/general';
import { CoordinateType, GeoLocationType, LocationType, MarkerClickEventPayloadType, } from '../constants/constants';
import { hasValidLocationHash, loadLocationListFromString, ROOT_CLASS as MAP_ROOT_CLASS, setCoordinate, setOffset, setVisibleListOfMarkers } from './map';
import { updateAccordionFilterLabel } from './accordion';
import { getAccordionNameList } from './filter-dialog';
import { loadCsrfToken } from './initialize-csrf-token';
import { renderOpenStatus } from './open-status';
import { createCoopTag } from './coop-tag';
import { createCoopIcon } from './coop-icon';

let postponeActivateLocationByHash = false;
let areAsynchronLoadingOfnMarkersCompleted = false;
let currentUserGeoLocation: CoordinateType | null = null;
let mapEl: HTMLFormElement | null = null;
let lockTriggerNewCoordinateChange = false;
let acceptedMarketCookies = false;
let isCookiebotActive = false;
let SwiperInstance: Swiper | null = null;
let usingGeolocation = true; // YANN
let searchStringsBefore: string = '';
let currentSiteId: number | null = null;

const ROOT_CLASS = 'location-overview';
const MOBILE_ITEM_MODE_CLASS = `${ROOT_CLASS}__mobile-item-mode`;
const SHARE_LOCATION_BUTTON_CLASS = `${ROOT_CLASS}__share-my-location`;
const ROUTE_BUTTON_CLASS = `${ROOT_CLASS}__route-button`;
const SELECTED_LOCATION_NUMBER_CLASS = `${ROOT_CLASS}__result-number`;
const DISTANCE_ITEM_CLASS = `${ROOT_CLASS}__item-distance`;
const LIST_CLASS = `${ROOT_CLASS}__location-list`;
const SWIPER_LIST_CLASS = `${ROOT_CLASS}__location-list-mobile`;
const SWIPER_CLASS = `${ROOT_CLASS}__swiper`;
const LIST_ITEM_CLASS = `${ROOT_CLASS}__list-item`;
const LIST_ITEM_SKELETON_CLASS = `${LIST_ITEM_CLASS}-skeleton`;
const FILTER_SERVICES_CLASS = `${ROOT_CLASS}__filter-services`;
const SELECTED_LOCATION_CLASS = `${ROOT_CLASS}__selected-location`;
const SELECTED_LOCATION_HIDDEN_CLASS = `${ROOT_CLASS}__selected-location--hidden`;
const LIST_LINK_CLASS = `${ROOT_CLASS}__item-link`;
const SEARCH_INPUT_CLASS = `${ROOT_CLASS}__search-input`;
const SEARCH_FORM_CLASS = `${ROOT_CLASS}__search-form`;
const LIST_ITEM_HIDDEN_CLASS = `${LIST_ITEM_CLASS}--hidden`;
const ALL_SELECTED_FILTER_LABEL_CLASS = `${ROOT_CLASS}__all-selected-filter-label`;

export const init = (rootEl: HTMLElement) => {
  isCookiebotActive = rootEl.dataset.cookiebotActive === 'true';
  acceptedMarketCookies = !isCookiebotActive ? true : isCookiebotActive;
  usingGeolocation = rootEl.dataset.geolocation === 'true' || true;
  currentSiteId = rootEl.dataset.siteId ? parseInt(rootEl.dataset.siteId, 10) : null;

  let geolocationInProcess = false;
  let clusterClickProcessActive = false;

  mapEl = rootEl.querySelector<HTMLFormElement>(`.${MAP_ROOT_CLASS}`);
  const searchForm = rootEl.querySelector<HTMLFormElement>(`.${SEARCH_FORM_CLASS}`);
  const listEl = rootEl.querySelector<HTMLUListElement>(`.${LIST_CLASS}`);
  const swiperListEl = rootEl.querySelector<HTMLUListElement>(`.${SWIPER_LIST_CLASS}`);
  const listLinkItems = listEl?.querySelectorAll<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
  const listSwiperLinkItems = swiperListEl?.querySelectorAll<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
  const mobileItemModeButton = rootEl.querySelector<HTMLButtonElement>(`.${MOBILE_ITEM_MODE_CLASS}`);
  const allFilterLabel = rootEl.querySelector<HTMLSpanElement>(`.${ALL_SELECTED_FILTER_LABEL_CLASS}`);
  const shareLocationButton = rootEl.querySelector<HTMLButtonElement>(`.${SHARE_LOCATION_BUTTON_CLASS}`);
  const swiper = rootEl.querySelector<HTMLButtonElement>(`.${SWIPER_CLASS}`);
  const filterServiceContainer = rootEl.querySelector<HTMLDivElement>(`.${FILTER_SERVICES_CLASS}`);

  let searchInput: null | HTMLInputElement = null;
  if (searchForm) {
    searchInput = searchForm.querySelector<HTMLInputElement>(`.${SEARCH_INPUT_CLASS}`);
    const formInputList = searchForm.querySelectorAll<HTMLInputElement>(`.${SEARCH_INPUT_CLASS}`);
    presetInputByUrlParams(formInputList);
    setTimeout(() => {
      if (searchInput) {
        searchStringsBefore = searchInput?.value;
      }
    }, 100)
  }

  if (shareLocationButton) {
    if (usingGeolocation && 'geolocation' in navigator) {
      shareLocationButton.addEventListener('click', () => {
        if (!geolocationInProcess) {
          geolocationInProcess = true;
          navigator.permissions.query({name: 'geolocation'}).then((result) => {
            if (result.state === 'granted' || result.state === 'prompt') {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  currentUserGeoLocation = {lat: position.coords.latitude, lng: position.coords.longitude};
                  if (listLinkItems) {
                    setDistanceToLinkItems(listLinkItems, currentUserGeoLocation);
                    sortLinkItemsByDistance(LIST_CLASS, true);
                  }
                  if (listSwiperLinkItems && SwiperInstance) {
                    setDistanceToLinkItems(listSwiperLinkItems, currentUserGeoLocation);
                    sortLinkItemsByDistance(SWIPER_LIST_CLASS, true);
                    SwiperInstance.update();
                  }
                  setClassForGeolocationFound(rootEl);
                  setCoordinate(currentUserGeoLocation, 14);
                  activateLocationByHash(rootEl);
                  if (searchForm) {
                    handleSearchInput(searchForm, rootEl);
                  }
                  geolocationInProcess = false;
                }
              );
            } else {
              geolocationInProcess = false;
              removeClassForGeolocationFound(rootEl);
            }
          });
        }
      });
    } else {
      // remove the button since the location is not supported or not wanted
      shareLocationButton.remove();
    }
  }

  createAllLocations(rootEl).then((locations) => {
    searchInListItems(rootEl, [searchInput?.value ?? '']);
    createLocationsAsStringToElement(rootEl, locations);

    // Only call loadLocationListFromString if mapEl exists and Google Maps isn't already initialized
    if (mapEl) {
      createLocationsAsStringToElement(mapEl, locations);
      // Ensure we don't try to initialize the map multiple times
      if (typeof google === 'undefined' || !google.maps) {
        loadLocationListFromString(mapEl);
      } else {
        // If Google Maps is already loaded, just update the markers
        const markerString = mapEl.dataset.markers ?? '';
        if (markerString) {
          loadLocationListFromString(mapEl);
        }
      }
    }
  });

  if (swiper) {
    // Hier muss noch aktueller hash als basis für selection genommen werden.
    const initialSlide = 0;

    SwiperInstance = new Swiper(swiper, {
      speed: 400,
      draggable: true,
      loop: false,
      effect: 'slide',
      containerModifierClass: `${SWIPER_CLASS}`,
      slideClass: `${ROOT_CLASS}__list-item`,
      wrapperClass: `${SWIPER_LIST_CLASS}`,
      slidesPerView: 1,
      initialSlide: initialSlide,
      spaceBetween: 10,
      updateOnWindowResize: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      modules: [Controller, Keyboard, EffectFade],
      on: {
        slideChange: (e) => {
          const slide = e.slides.filter(s => !s.classList.contains(`${LIST_ITEM_CLASS}--hidden`))[e.activeIndex];
          if (slide) {
            const slideLink = slide.querySelector<HTMLLinkElement>('a');
            if (slideLink) {
              window.location.hash = slideLink.href.split('#')[1];
            }
          }
        },
      }
    } as SwiperOptions);
  }

  if (mobileItemModeButton) {
    mobileItemModeButton.addEventListener('click', () => {
      rootEl.classList.toggle(`${ROOT_CLASS}--mobile-item-mode`);
    });
  }

  if (listLinkItems) {
    listLinkItems.forEach((listLinkItem) => {
      listLinkItem.addEventListener('click', (e) => {
        const hash = window.location.hash.split('#')[1] ?? '';
        const linkHash = (e.currentTarget as HTMLLinkElement).href.split('#')[1];
        if (hash === linkHash && mapEl) {
          setHash();
          removeUriParamQuery();
          mapEl.reset();
          e.preventDefault();
        }
      });
    });
  }

  // if there was some events while loading markers asynchronously, call all methods from the events again
  rootEl.addEventListener('markers-loaded', () => {
    if (postponeActivateLocationByHash) {
      activateLocationByHash(rootEl);
    }
  });

  window.addEventListener('CookiebotOnAccept', function () {
    acceptedMarketCookies = true;
    if (searchForm) {
      handleSearchInput(searchForm, rootEl);
    }
  });

  detectMobileMapOffset();
  window.addEventListener('resize', () => {
    detectMobileMapOffset();
  }, false);

  activateLocationByHash(rootEl);
  window.addEventListener('hashchange', () => {
    activateLocationByHash(rootEl);
  }, false);

  if (mapEl) {
    mapEl.addEventListener('geolocation-found', async (e: any) => {
      const userLocation = e.detail as CoordinateType;
      currentUserGeoLocation = userLocation;
      if (listLinkItems) {
        setDistanceToLinkItems(listLinkItems, userLocation);
        sortLinkItemsByDistance(LIST_CLASS, true);
      }
      if (listSwiperLinkItems) {
        setDistanceToLinkItems(listSwiperLinkItems, userLocation);
        sortLinkItemsByDistance(SWIPER_LIST_CLASS, true);
      }
      setClassForGeolocationFound(rootEl);

      if (searchForm) {
        handleSearchInput(searchForm, rootEl);
      }
    });

    mapEl.addEventListener('marker-click', (e: any) => {
      const payload = e.detail as MarkerClickEventPayloadType;
      const hash = `${payload.location.slug}-${payload.location.id}`;
      const listLinkItem = rootEl
        .querySelector<HTMLLinkElement>(`[href="#${hash}"]`);
      if (listLinkItem) {
        listLinkItem.click();
      }
    });

    if (searchForm) {
      setFilterInUrlParam(searchForm);
      if (filterServiceContainer) {
        const inputList = filterServiceContainer.querySelectorAll<HTMLInputElement>(`.chip input`);
        presetInputByUrlParams(inputList, () => {
          if (allFilterLabel) {
            setTimeout(() => {
              updateAccordionFilterLabel(rootEl, getAccordionNameList(searchForm), allFilterLabel);
              setVisibleListOfMarkers(getVisibleListItems(rootEl));
            }, 300);
          }
        });
      }
      searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        if (allFilterLabel) {
          updateAccordionFilterLabel(rootEl, getAccordionNameList(searchForm), allFilterLabel);
          setVisibleListOfMarkers(getVisibleListItems(rootEl));
        }
      });

      if (searchInput) {
        searchInput.addEventListener('input', () => {
          handleSearchInput(searchForm, rootEl);
          setVisibleListOfMarkers(getVisibleListItems(rootEl));
        });

        // if a value is preset by page load
        if (searchInput.value.length > 0) {
          handleSearchInput(searchForm, rootEl);
          setVisibleListOfMarkers(getVisibleListItems(rootEl));
        }
      }

      searchForm.addEventListener('change', () => {
        if (allFilterLabel) {
          updateAccordionFilterLabel(rootEl, getAccordionNameList(searchForm), allFilterLabel);
          searchInListItems(rootEl, [searchInput?.value ?? '']);
          setFilterInUrlParam(searchForm);
        }
        setVisibleListOfMarkers(getVisibleListItems(rootEl));
      });

      searchForm.addEventListener('reset', () => {
        if (searchInput) {
          searchInput.setAttribute('value', '');
        }
        setHash();
        removeUriParamQuery();
        handleExtendedSearchOpen(rootEl, 1);
        removeClassForGeolocationFound(rootEl);
        setVisibleListOfMarkers(getVisibleListItems(rootEl));
        if (mapEl) {
          mapEl.reset();
        }
      });
    }

    mapEl.addEventListener('zoom-changed', () => {
      if (!clusterClickProcessActive && !lockTriggerNewCoordinateChange) {
        // I deactivated these settings because it looks like the not usesr friendly
        // if they are still needed, please activate them again
        // resetListItems(rootEl);
        // deactivateListItems(rootEl);
      } else {
        setTimeout(() => {
          clusterClickProcessActive = false;
          lockTriggerNewCoordinateChange = false;
        }, 100);
      }
    });
  }
}

const getVisibleListItems = (rootEl: HTMLElement, listClass: string = LIST_CLASS): number[] => {
  const listEl = rootEl.querySelector<HTMLUListElement>(`.${listClass}`);
  if (listEl) {
    const visibleListItems = listEl.querySelectorAll<HTMLLIElement>(`.${LIST_ITEM_CLASS}:not(.${LIST_ITEM_HIDDEN_CLASS})`);
    if (visibleListItems) {
      let visibleListItemsIds: number[] = [];
      visibleListItems.forEach((item) => {
        visibleListItemsIds.push(parseInt(item.dataset.id ?? '0'));
      });
      return visibleListItemsIds;
    }
  }
  return []
}

export const loadOpenTimes = async (ids: number[] = []): Promise<{ id: number, minutesLeft: number }[]> => {
  const url = `/get-remaining-open-minutes`;

  const csrfToken = await loadCsrfToken();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ids})
    });

    return await response.json() as { id: number, minutesLeft: number }[];
  } catch (error) {
    return [];
  }
}

const createLocationsAsStringToElement = (elementToSaveString: HTMLElement | null, locationList: LocationType[]): string => {
  if (elementToSaveString) {
    const locationString = locationList.map((location) =>
      location.id + ',' + location.posLat + ',' + location.posLng + ',' + location.name + ',' + slugify(location.slug)
    ).join('|');
    elementToSaveString.dataset.markers = locationString;

    return locationString;
  }

  return '';
}

const appendLocationItems = (rootEl: HTMLElement, resultLocation: LocationType[]) => {
  const listEl = rootEl.querySelector(`.${LIST_CLASS}`) as HTMLDivElement;
  const listSwiperEl = rootEl.querySelector(`.${SWIPER_LIST_CLASS}`) as HTMLDivElement;

  if (listEl && listSwiperEl) {
    // Remove skeleton loaders when first real items are being added
    removeSkeletonItems(rootEl, resultLocation);

    resultLocation.forEach((locationEntry) => {
      const locationCard = createLocationCard(locationEntry);
      if (locationCard) {
        listEl.appendChild(locationCard);
      }

      const currentLinkEl = listEl.querySelector<HTMLLinkElement>(`[href="#${locationEntry.slug}-${locationEntry.id}"]`);
      if (currentLinkEl) {
        currentLinkEl.addEventListener('click', (e) => {
          const hash = window.location.hash.split('#')[1] ?? '';
          const linkHash = (e.currentTarget as HTMLLinkElement).href.split('#')[1];
          if (hash === linkHash && isMobileView()) {
            rootEl.classList.remove(`${ROOT_CLASS}--mobile-item-mode`);
            e.preventDefault();
          }
        });
      }

      const locationCardSwiper = createLocationCard(locationEntry, true);

      if (locationCardSwiper) {
        listSwiperEl.appendChild(locationCardSwiper);
      }
    });
  }
};

const removeSkeletonItems = (rootEl: HTMLElement, resultLocation: LocationType[]) => {
  const skeletonItems = rootEl.querySelectorAll(`.${LIST_ITEM_SKELETON_CLASS}`);
  if (skeletonItems.length > 0 && resultLocation.length > 0) {
    skeletonItems.forEach(item => {
      item.remove();
    });
  }
}

const createAllLocations = async (rootEl: HTMLElement) => {

  // Try to get user location automatically if geolocation is available
  if ('geolocation' in navigator && !currentUserGeoLocation) {
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          maximumAge: 60000
        });
      });

      currentUserGeoLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };

      setClassForGeolocationFound(rootEl);
    } catch (error) {
      console.log('Geolocation not available or denied');
    }
  }

  let allLocations: LocationType[] = [];

  const firstBatch = await loadAllLocations(10);
  allLocations = [...firstBatch];
  appendLocationItems(rootEl, firstBatch);
  sortAndUpdateLocations(rootEl);

  const secondBatch = await loadAllLocations(90, 10);
  allLocations = [...allLocations, ...secondBatch];
  appendLocationItems(rootEl, secondBatch);
  sortAndUpdateLocations(rootEl);

  const remainingBatch = await loadAllLocations(10000, 100);
  allLocations = [...allLocations, ...remainingBatch];
  appendLocationItems(rootEl, remainingBatch);
  sortAndUpdateLocations(rootEl);

  // Update map with all locations
  createLocationsAsStringToElement(rootEl, allLocations);
  createLocationsAsStringToElement(mapEl, allLocations);
  loadLocationListFromString(mapEl);

  areAsynchronLoadingOfnMarkersCompleted = true;
  rootEl.dispatchEvent(new CustomEvent('markers-loaded'));
  return allLocations;
}

// Helper function to sort and update locations based on user location
const sortAndUpdateLocations = (rootEl: HTMLElement) => {
  const listEl = rootEl.querySelector<HTMLUListElement>(`.${LIST_CLASS}`);
  const swiperListEl = rootEl.querySelector<HTMLUListElement>(`.${SWIPER_LIST_CLASS}`);

  if (currentUserGeoLocation) {
    if (listEl) {
      const listLinkItems = listEl.querySelectorAll<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
      setDistanceToLinkItems(listLinkItems, currentUserGeoLocation);
      sortLinkItemsByDistance(LIST_CLASS, true);
    }

    if (swiperListEl) {
      const listSwiperLinkItems = swiperListEl.querySelectorAll<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
      setDistanceToLinkItems(listSwiperLinkItems, currentUserGeoLocation);
      sortLinkItemsByDistance(SWIPER_LIST_CLASS, true);

      if (SwiperInstance) {
        SwiperInstance.update();
      }
    }
  } else {
    // If no user location, sort alphabetically
    sortLinkItemsByDistance(LIST_CLASS, false);
    sortLinkItemsByDistance(SWIPER_LIST_CLASS, false);
    if (SwiperInstance) {
      SwiperInstance.update();
    }
  }
}

const loadAllLocations = async (limit: number = 50, offset: number = 0): Promise<LocationType[]> => {
  const url = `/load-all-locations`;
  const csrfToken = await loadCsrfToken();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        limit,
        offset,
        siteId: currentSiteId,
        'userCoordinates': currentUserGeoLocation
      })
    });

    return await response.json() as LocationType[];
  } catch (error) {
    console.error(error);
    return [];
  }
}

export const renderOpenTimeTags = (rootEl: HTMLElement, openTimes: { id: number, minutesLeft: number }[], singleOpenTime: HTMLElement | null = null) => {
  const listItems = selectListItems(rootEl);
  const listSwiperItems = selectListItems(rootEl, SWIPER_LIST_CLASS);
  const selectedItem = rootEl.querySelector<HTMLDivElement>(`.${SELECTED_LOCATION_CLASS}-content`);

  const renderListItems = (listItems: NodeListOf<HTMLLIElement>) => {
    listItems.forEach((listItem) => {
      const linkItem = listItem.querySelector<HTMLDivElement>(`.${LIST_LINK_CLASS}`);
      const openTime = openTimes.find((openTime) =>
        openTime.id === parseInt(listItem.dataset.id ?? '0')
      );
      if (linkItem && openTime) {
        linkItem.querySelector<HTMLDivElement>(`.${ROOT_CLASS}__address-tag-wrapper > .tag`)?.remove();
        linkItem.querySelector<HTMLDivElement>(`.${ROOT_CLASS}__address-tag-wrapper`)
          ?.prepend(renderStringAsHtml(renderOpenStatus(openTime.minutesLeft)));
      }
    });
  }

  if (singleOpenTime && openTimes.length === 1) {
    const oldClassNamesWithoutDisabled = singleOpenTime.classList.value.replace('tag--disabled', '');
    singleOpenTime.parentElement?.prepend(renderStringAsHtml(renderOpenStatus(openTimes[0].minutesLeft, oldClassNamesWithoutDisabled)));
    singleOpenTime.remove();
  }

  if (listItems) {
    renderListItems(listItems);
  }

  if (listSwiperItems) {
    renderListItems(listSwiperItems);
  }

  if (selectedItem) {
    const openTime = openTimes.find((openTime) =>
      openTime.id === parseInt(selectedItem.dataset.id ?? '0')
    );
    if (openTime) {
      selectedItem.querySelector<HTMLDivElement>(`.tag`)?.remove();
      selectedItem.prepend(renderStringAsHtml(renderOpenStatus(openTime.minutesLeft)));
    }
  }
}

const activateLocationByHash = async (rootEl: HTMLElement, listClass: string = LIST_CLASS) => {
  const selectedLocationInfo = rootEl.querySelector<HTMLDivElement>(`.${SELECTED_LOCATION_CLASS}`);
  if (areAsynchronLoadingOfnMarkersCompleted) {
    const linkListEl = rootEl.querySelector<HTMLUListElement>(`.${listClass}`);
    const container = rootEl.querySelector<HTMLDivElement>(`.${SELECTED_LOCATION_CLASS}-content`);
    const hash = window.location.hash.split('#')[1] ?? '';
    const listLinkItem = linkListEl?.querySelector<HTMLLinkElement>(`[href="#${hash}"]`);

    if (listLinkItem && container) {
      const routeButtonMobile = rootEl.querySelector(`.${ROUTE_BUTTON_CLASS}`) as HTMLLinkElement;
      const routeButtonDesktop = container.nextElementSibling as HTMLLinkElement;
      rootEl.classList.add(`${ROOT_CLASS}--selected-location-visible`);
      const locationLink = listLinkItem.nextElementSibling as HTMLLinkElement;
      const lat = parseFloat(listLinkItem.dataset.lat ?? '0');
      const lng = parseFloat(listLinkItem.dataset.lng ?? '0');
      routeButtonMobile.href = setRouteCoordinates(lat, lng);
      routeButtonDesktop.href = setRouteCoordinates(lat, lng);
      container.innerHTML = `${listLinkItem.outerHTML} ${locationLink.outerHTML}`;
      container.dataset.id = listLinkItem.parentElement?.dataset.id ?? '';

      if (isMobileView()) {
        if (SwiperInstance) {
          const swiperListEl = rootEl.querySelector<HTMLUListElement>(`.${SWIPER_LIST_CLASS}`)!;
          const slides = [...swiperListEl.children] as HTMLElement[];
          const foundSlides = Array.from(slides)
            .filter((e) => !e.classList.contains(LIST_ITEM_HIDDEN_CLASS));

          foundSlides.forEach((slide, index) => {
            const slideLink = slide.firstElementChild as HTMLElement;
            const elemHash = (slideLink.dataset.slug ?? '') + '-' + slide.dataset.id;

            if (elemHash === hash && SwiperInstance) {
              if (SwiperInstance.slides.length > 0) {
                SwiperInstance.slideTo(index);
              }

              rootEl.classList.remove(`${ROOT_CLASS}--mobile-item-mode`);
            }
          });
          SwiperInstance.updateSlides();
        }
      }
      selectedLocationInfo?.classList.remove(SELECTED_LOCATION_HIDDEN_CLASS);
    } else {
      selectedLocationInfo?.classList.add(SELECTED_LOCATION_HIDDEN_CLASS);
    }
  } else {
    postponeActivateLocationByHash = true;
  }
}

const setRouteCoordinates = (lat: number, lng: number) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

const setClassForGeolocationFound = (rootEl: HTMLElement) => {
  rootEl.classList.add(`${ROOT_CLASS}--geolocation-found`);
}

const removeClassForGeolocationFound = (rootEl: HTMLElement) => {
  rootEl.classList.remove(`${ROOT_CLASS}--geolocation-found`);
}

const removeUriParamQuery = (): void => {
  history.pushState('', document.title, window.location.pathname);
}

const selectListItems = (rootEl: HTMLElement, listClass: string = LIST_CLASS): NodeListOf<HTMLLIElement> | null => {
  const listEl = rootEl.querySelector<HTMLUListElement>(`.${listClass}`);
  if (listEl) {
    return listEl.querySelectorAll<HTMLLIElement>(`.${LIST_ITEM_CLASS}`);
  }

  return null;
}

const hideAllListItems = (listItems: NodeListOf<HTMLLIElement>): void => {
  listItems.forEach((listItem) =>
    listItem.classList.add(LIST_ITEM_HIDDEN_CLASS));
}

const showAmountOfListItems = (rootEl: HTMLElement, amount: number = 10, maxDistance: number = 30): number => {
  const listItems = selectListItems(rootEl);
  if (listItems) {
    hideAllListItems(listItems);

    let foundAmount = 0;
    listItems.forEach((listItem, index: number) => {
      const distance = parseFloat(listItem.querySelector<HTMLLinkElement>(`.${DISTANCE_ITEM_CLASS}`)?.dataset.distance ?? '0');
      if (index < amount && maxDistance > distance) {
        listItem.classList.remove(LIST_ITEM_HIDDEN_CLASS);
        foundAmount++;
      }
    });

    return foundAmount;
  }

  return 0;
}

const setAmountOfFoundLocations = (rootEl: HTMLElement, foundAmount: number) => {
  const selectedLocationLi = rootEl.querySelectorAll<HTMLDivElement>(`.${SELECTED_LOCATION_NUMBER_CLASS}`);
  if (selectedLocationLi) {
    selectedLocationLi.forEach((resultContainer) => {
      resultContainer.innerText = foundAmount.toString();
    });
  }
};

const hasLocationService = (serviceString: string, selectedServices: number[]): boolean => {
  if (selectedServices.length === 0) {
    return true;
  }

  const services = serviceString.split(',');
  for (const service of services) {
    if (selectedServices.indexOf(parseInt(service)) !== -1) {
      return true;
    }
  }

  return false;
};

const getSelectedServices = (rootEl: HTMLElement): number[] => {
  const selectedServices: number[] = [];
  rootEl.querySelectorAll<HTMLInputElement>(`[type="checkbox"]:checked`).forEach((checkbox) => {
    if (selectedServices.indexOf(parseInt(checkbox.value)) === -1) {
      selectedServices.push(parseInt(checkbox.value));
    }
  });

  return selectedServices;
};

const searchInListItems = (rootEl: HTMLElement, searchStrings: string[], callback?: (foundAmount: number) => void) => {
  const isNewSearchString = searchStringsBefore !== searchStrings[0];
  const hasSearchString = searchStrings.length > 0;
  const listItems = selectListItems(rootEl);
  const selectedServices = getSelectedServices(rootEl);

  if (listItems) {
    // hide items in desktop List
    hideAllListItems(listItems);
    let foundAmount = 0;
    listItems.forEach((listItem) => {
      // show items with search text in desktop List
      const searchText = listItem.dataset.searchValue ?? '';
      searchStrings.forEach((search) => {
        const serviceString = listItem.dataset.services ?? '';
        const hasLocationServiceResult = hasLocationService(serviceString, selectedServices);
        if (searchText.toLowerCase().includes(search.toLowerCase()) && hasLocationServiceResult) {
          if (listItem.classList.contains(LIST_ITEM_HIDDEN_CLASS)) {
            foundAmount++;
          }
          listItem.classList.remove(LIST_ITEM_HIDDEN_CLASS);
        }
      });
    });

    const listSwiperItems = selectListItems(rootEl, SWIPER_LIST_CLASS);
    if (listSwiperItems) {
      // hide items in mobile List
      hideAllListItems(listSwiperItems);
      let foundAmountMobile = 0;
      listSwiperItems.forEach((listSwiperItem) => {
        // show items with search text in mobile List
        const searchText = listSwiperItem.dataset.searchValue ?? '';
        searchStrings.forEach((search) => {
          const serviceString = listSwiperItem.dataset.services ?? '';
          const hasLocationServiceResult = hasLocationService(serviceString, selectedServices);
          if (searchText.toLowerCase().includes(search.toLowerCase()) && hasLocationServiceResult) {
            if (listSwiperItem.classList.contains(LIST_ITEM_HIDDEN_CLASS)) {
              foundAmountMobile++;
            }
            listSwiperItem.classList.remove(LIST_ITEM_HIDDEN_CLASS);
          }
        });
      });
      if (SwiperInstance) {
        SwiperInstance.update();
        if (foundAmountMobile > 0 && isMobileView()) {
          const found = Array.from(listSwiperItems)
            .filter((e) => !e.classList.contains(LIST_ITEM_HIDDEN_CLASS));
          const markerString = rootEl.dataset.markers ?? '';
          const locationList = stringToLocationList(markerString);
          if (found.length > 0 && locationList.length > 0) {
            hasValidLocationHash(locationList).then((validLocation) => {
              if (validLocation) {
                Array.from(listSwiperItems).forEach((e) => {
                  if (parseInt(e.dataset.id ?? '-1') === validLocation.id) {
                    if (hasSearchString && !isNewSearchString) {
                      found.forEach((foundItem, index) => {
                        if (foundItem.dataset.id === e.dataset.id) {
                          SwiperInstance?.slideTo(index);
                        }
                      });
                    } else if (isNewSearchString) {
                      SwiperInstance?.slideTo(0);
                    }
                  }
                });
              }
            });

            const searchInput = rootEl.querySelector<HTMLInputElement>(`.${SEARCH_INPUT_CLASS}`);
            let searchInputValue = searchInput?.value ?? '';
            if(searchInputValue){
              rootEl.classList.add(`${ROOT_CLASS}--selected-location-visible`);
              const selectedLocationInfo = rootEl.querySelector<HTMLDivElement>(`.${SELECTED_LOCATION_CLASS}`);
              selectedLocationInfo?.classList.remove(SELECTED_LOCATION_HIDDEN_CLASS);
            }
          }
        }
      }
    }

    setAmountOfFoundLocations(rootEl, foundAmount);

    if (callback) {
      callback(foundAmount);
    }
  }
}

const handleExtendedSearchOpen = (rootEl: HTMLElement, foundAmount: number) => {
  const hiddenCookieConsentClass = `${ROOT_CLASS}__extended-search`;
  const hiddenCookieEl = rootEl.querySelector<HTMLLinkElement>(`.${hiddenCookieConsentClass}`);

  if (foundAmount === 0) {
    hiddenCookieEl?.classList.add(`${hiddenCookieConsentClass}--show`);
  } else {
    hiddenCookieEl?.classList.remove(`${hiddenCookieConsentClass}--show`);
  }
};

function handleSearchInput(searchForm: HTMLFormElement, rootEl: HTMLElement) {
  const searchInput = searchForm.querySelector<HTMLInputElement>(`.${SEARCH_INPUT_CLASS}`);
  if (searchInput) {
    const searchValue = searchInput.value;
    const listEl = rootEl.querySelector<HTMLUListElement>(`.${LIST_CLASS}`);
    const listSwiperEl = rootEl.querySelector<HTMLUListElement>(`.${SWIPER_LIST_CLASS}`);
    if (currentUserGeoLocation && listEl && listSwiperEl) {
      const listLinkItems = listEl.querySelectorAll<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
      const listSwiperLinkItems = listSwiperEl.querySelectorAll<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
      if (isMobileView()) {
        setDistanceToLinkItems(listSwiperLinkItems, currentUserGeoLocation);
        sortLinkItemsByDistance(SWIPER_LIST_CLASS, true);
      } else {
        setDistanceToLinkItems(listLinkItems, currentUserGeoLocation);
        sortLinkItemsByDistance(LIST_CLASS, true);
      }

      setClassForGeolocationFound(rootEl);
    }

    if (currentUserGeoLocation === null) {
      if (isMobileView()) {
        sortLinkItemsByDistance(SWIPER_LIST_CLASS, false);
      } else {
        sortLinkItemsByDistance(LIST_CLASS, false);
      }
    }
    searchInListItems(rootEl, [searchValue], (foundAmount) => {
      handleExtendedSearchOpen(rootEl, foundAmount);
      if (foundAmount) {
        searchInListItems(rootEl, [searchValue]);
      } else if (acceptedMarketCookies) {
        getGeocodeByAddress(rootEl, searchValue).then((response) => {
          if (response) {
            const listEl = rootEl.querySelector<HTMLUListElement>(`.${LIST_CLASS}`);
            if (listEl && listSwiperEl) {

              if (isMobileView()) {
                const listSwiperLinkItems = listSwiperEl.querySelectorAll<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
                setDistanceToLinkItems(listSwiperLinkItems, response.geometry.location);
                sortLinkItemsByDistance(SWIPER_LIST_CLASS, true);
              } else {
                const listLinkItems = listEl.querySelectorAll<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
                setDistanceToLinkItems(listLinkItems, response.geometry.location);
                sortLinkItemsByDistance(LIST_CLASS, true);
              }

              const foundNearApoAmount = showAmountOfListItems(rootEl, 10, 50);
              if (foundNearApoAmount > 0) {
                setClassForGeolocationFound(rootEl);
                setAmountOfFoundLocations(rootEl, foundNearApoAmount);
                if (!lockTriggerNewCoordinateChange) {
                  lockTriggerNewCoordinateChange = true;
                  setTimeout(() => {
                    setCoordinate(response.geometry.location);
                  }, 1000);
                }
              } else {
                removeClassForGeolocationFound(rootEl);
              }
            }
          }
        });
      } else {
        handleExtendedSearchOpen(rootEl, foundAmount);
        removeClassForGeolocationFound(rootEl);
      }
      setSearchValue(rootEl, searchValue);
      setFilterInUrlParam(searchForm);
    });
    searchStringsBefore = searchValue;
  }
}

const setSearchValue = (rootEl: HTMLElement, searchValue: string) => {
  const searchStringLi = rootEl.querySelectorAll<HTMLDivElement>(`.${ROOT_CLASS}__search-string`);
  searchStringLi.forEach((searchStringEl) => {
    searchStringEl.innerText = searchValue;
  });
};

/**
 * Sorts the list items by distance
 * @param listClass
 * @param sortByDistance
 */
const sortLinkItemsByDistance = (listClass: string = LIST_CLASS, sortByDistance: boolean = false) => {
  const listEl = document.querySelector<HTMLUListElement>(`.${listClass}`);
  if (listEl) {
    const listItem = Array.from(listEl.querySelectorAll<HTMLLIElement>(`.${LIST_ITEM_CLASS}`))
    listItem.sort((a, b) => {
      if (a && b) {
        const linkItemA = a.querySelector<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
        const linkItemB = b.querySelector<HTMLLinkElement>(`.${LIST_LINK_CLASS}`);
        if (linkItemA && linkItemB) {
          if (sortByDistance) {
            const distanceA = parseFloat(linkItemA.dataset.distance ?? '0');
            const distanceB = parseFloat(linkItemB.dataset.distance ?? '0');

            return distanceA - distanceB;
          } else {
            const searchValueA = linkItemA.dataset.searchValue?.toLowerCase() ?? '';
            const searchValueB = linkItemB.dataset.searchValue?.toLowerCase() ?? '';

            // locale compare for non ascii characters
            return searchValueA.localeCompare(searchValueB);
          }
        }
      }
      return 0;
    });

    listEl.innerHTML = '';
    listItem.forEach((item) => {
      listEl.appendChild(item);
    });
  }
};

const setDistanceToLinkItems = (listLinkElements: NodeListOf<HTMLLinkElement>, userLocation: CoordinateType) => {
  listLinkElements.forEach((listLinkElement) => {
    const lat = parseFloat(listLinkElement.dataset.lat ?? '0');
    const lng = parseFloat(listLinkElement.dataset.lng ?? '0');
    const distanceEl = listLinkElement.querySelector<HTMLSpanElement>(`.${DISTANCE_ITEM_CLASS}`);
    if (distanceEl) {
      const distance = calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
      listLinkElement.dataset.distance = distance.toString();

      if (distance > 5) {
        distanceEl.dataset.distance = distance.toFixed(0).toString();
      } else {
        distanceEl.dataset.distance = distance.toFixed(2).toString();
      }

      if (distance < 1) {
        distanceEl.innerText = `${(distance * 1000).toFixed(0)} m`;
      } else {
        distanceEl.innerText = `${distance.toFixed(0)} km`;
      }
    }
  });
}

const getGeocodeByAddress = async (rootEl: HTMLElement, place: string): Promise<GeoLocationType | null> => {
  if ((isCookiebotActive && !acceptedMarketCookies) || !place || !rootEl) {
    return null;
  }

  const geocodeApiKey = rootEl.dataset.key ?? '';
  if (geocodeApiKey === '') {
    return null;
  }

  const link = 'https://maps.googleapis.com/maps/api/geocode/json?key='
    + geocodeApiKey
    + '&address=' + place;

  const responseFromCache = getResponseFromLocalStorage(link);
  if (responseFromCache !== null && responseFromCache !== 'undefined') {
    return JSON.parse(responseFromCache) as GeoLocationType;
  }

  return fetch(link)
    .then(response => response.json())
    .then(data => {
      setResponseToLocalStorage(link, data.results[0]);

      return data.results[0] as GeoLocationType
    });
}

const getResponseFromLocalStorage = (link: string): string | null => {
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(link);
  }
  return null;
}

const setResponseToLocalStorage = (link: string, data: any) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(link, JSON.stringify(data));
  }
}

const detectMobileMapOffset = () => {
  if (isMobileView()) {
    setOffset(0, window.innerHeight / 5);
  } else {
    setOffset(0, 0);
  }
};

const generateSearchString = (location: LocationType) => {
  return `${location.name}${location.zip}${location.city}`;
}

export const createLocationCard = (location: LocationType, hasGoogleRouteLink: boolean = false) => {
  const googleRouteLink = `
    <a
      href="https://www.google.com/maps/dir/?api=1&destination=${location.posLat},${location.posLng}"
      class="button button--icon-only location-overview__route-button"
      target="_blank"
      rel="noopener noreferrer"
    >
      ${createCoopIcon('directions')}
    </a>`;
  const distanceDOM = '<div class="location-overview__item-distance"></div>';
  const listItemTag = `
  <li class="location-overview__list-item"
    data-id="${location.id}"
    data-services="${location.services.join(',')}"
    data-search-value="${generateSearchString(location)}"
  >
    <a
      href="#${slugify(location.slug + '-' + location.id)}"
      data-lat="${location.posLat}"
      data-lng="${location.posLng}"
      data-slug="${location.slug}"
      data-search-value="${generateSearchString(location)}"
      class="${LIST_LINK_CLASS} no-style"
    >
      <div class="location-overview__address-tag-wrapper">
        ${renderOpenStatus(location.minutesLeft)}
        ${createCoopTag('', 'distance', false, distanceDOM)}
      </div>
      <h4 class="${ROOT_CLASS}__item-name">${location.name}</h4>
      <div class="${ROOT_CLASS}__item-address">
        ${location.address}<br>
        ${location.zip} ${location.city}<br>
      </div>
    </a>
    ${hasGoogleRouteLink ? googleRouteLink : ''}
    <a
      href="${location.url}"
      class="location-overview__detail-link button button--icon-only button--teaser-icon button--just-link"
    >
      ${createCoopIcon('chevron-right')}
    </a>
  </li>`;

  return renderStringAsHtml<HTMLLIElement>(listItemTag);
}
